import { skipToken, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import { REAPPRO_ENABLED_STORE_STATUSES, type StoreStatus } from '@gozoki/api-types'
import { gozokiApiWithAuth } from '../gozokiApi'
import {
  importReapproNeedModifications,
  importReapproNeedModificationsBulk,
} from '../mutations/stores.mutations'
import {
  exportDeliveryRecapXlsx,
  exportReapproSimulationAllStoresXlsx,
  exportReapproSimulationXlsx,
  fetchAntiGaspi,
  fetchBackOfficeBoutiques,
  fetchBoutiques,
  fetchCpiInventorySummary,
  fetchPromoProducts,
  fetchStoreActionLogs,
  fetchStoreArticles,
  fetchStoreCategoryProducts,
  fetchStoreNewProducts,
  fetchStoreProductArticles,
  fetchStoreProducts,
  fetchStoreStock,
  fetchStoresStocks,
  fetchStoresmMetaDatas,
  requestDispatchSimulationAllStoresXlsxByEmail,
  requestReapproSimulationAllStoresXlsxByEmail,
} from '../queries/stores.queries'

export const useBoutiques = () =>
  useQuery({
    queryKey: ['stores', 'boutiques'],
    queryFn: () => fetchBoutiques(),
  })

export const useBackOfficeBoutiques = () =>
  useQuery({
    queryKey: ['stores', 'back-office-boutiques'],
    queryFn: () => fetchBackOfficeBoutiques(),
  })

export const useReapproBoutiques = () =>
  useQuery({
    queryKey: ['stores', 'reappro-boutiques'],
    queryFn: () => fetchBackOfficeBoutiques(),
    select: (stores) =>
      stores.filter((store) =>
        (REAPPRO_ENABLED_STORE_STATUSES as readonly StoreStatus[]).includes(store.status),
      ),
  })

export const useStoreArticles = (id: number) =>
  useQuery({
    queryKey: ['store', id, 'articles'],
    queryFn: () => {
      if (Number.isNaN(id)) {
        return []
      }
      return fetchStoreArticles(id)
    },
  })

export const useAntiGaspi = (id?: number) =>
  useInfiniteQuery({
    queryKey: ['store', id, 'antigaspi'],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) => {
      if (Number.isNaN(id) || !id) {
        return { data: [], hasMorePage: false }
      }
      return fetchAntiGaspi(id, pageParam as number)
    },
    initialPageParam: 1,
    getNextPageParam: (_lastPage, allPages) => allPages.length + 1,
  })

export const usePromoProducts = (id?: number) =>
  useInfiniteQuery({
    queryKey: ['store', id, 'promo-products'],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) => {
      if (Number.isNaN(id) || !id) {
        return { data: [], hasMorePage: false }
      }
      return fetchPromoProducts(id, pageParam as number)
    },
    initialPageParam: 1,
    getNextPageParam: (_lastPage, allPages) => allPages.length + 1,
  })

export const useStoreCategoryProducts = (id: number, cat: number) =>
  useInfiniteQuery({
    queryKey: ['store', id, 'category-products', cat],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) => {
      if (Number.isNaN(id)) {
        return { data: [], hasMorePage: false }
      }
      return fetchStoreCategoryProducts(id, cat, pageParam as number)
    },
    initialPageParam: 1,
    getNextPageParam: (_lastPage, allPages) => allPages.length + 1,
  })

export const useStoreProducts = (storeId: number) =>
  useQuery({
    queryKey: ['store', storeId, 'products'],
    queryFn: Number.isNaN(storeId) || storeId === 0 ? skipToken : () => fetchStoreProducts(storeId),
  })

export const useStoreNewProducts = (storeId: number) =>
  useInfiniteQuery({
    queryKey: ['store', storeId, 'new-products'],
    queryFn:
      Number.isNaN(storeId) || storeId === 0
        ? skipToken
        : ({ pageParam = 1 }: { pageParam?: number }) =>
            fetchStoreNewProducts(storeId, pageParam as number),
    initialPageParam: 1,
    getNextPageParam: (_lastPage, allPages) => allPages.length + 1,
  })

export const useStoreStock = (id: number, enabled = true) =>
  useQuery({
    queryKey: ['store', id, 'stock'],
    queryFn: Number.isNaN(id) || id === 0 ? skipToken : () => fetchStoreStock(id),
    enabled,
    staleTime: 30_000,
  })

export const useStoresStocks = (ids: number[] | undefined) =>
  useQuery({
    queryKey: ['stores', 'stocks', ids],
    queryFn: async () => {
      if (!ids || ids.length === 0) {
        return [
          {
            metadata: {
              articleCount: 0,
              antiGaspiArticleCount: 0,
              expiredArticleCount: 0,
              totalStockValue: 0,
              totalAntiGaspiStockValue: 0,
              totalExpiredStockValue: 0,
            },
            rows: [],
          },
        ]
      }
      return fetchStoresStocks(ids)
    },
    staleTime: 30_000,
  })
export const useStoresMetadata = (ids: number[] | undefined) =>
  useQuery({
    queryKey: ['stores', 'metadata', ids],
    queryFn: async () => {
      if (!ids || ids.length === 0) {
        return [
          {
            metadata: {
              articleCount: 0,
              antiGaspiArticleCount: 0,
              expiredArticleCount: 0,
              totalStockValue: 0,
              totalAntiGaspiStockValue: 0,
              totalExpiredStockValue: 0,
            },
          },
        ]
      }
      return fetchStoresmMetaDatas(ids)
    },
  })

export const useStoreProductArticles = (storeId: number, productId: number) =>
  useQuery({
    queryKey: ['store', storeId, 'product', productId],
    queryFn: () => {
      if (Number.isNaN(storeId) || Number.isNaN(productId)) {
        return []
      }
      return fetchStoreProductArticles(storeId, productId)
    },
  })

export const useReapprosExport = () =>
  useMutation({
    mutationFn: async () => {
      const res = await gozokiApiWithAuth.get<Blob>('/stores/reappro-needs/export-xlsx', {
        responseType: 'blob',
      })

      return res.data
    },
  })

export const useDeliveryRecapExport = () =>
  useMutation({
    mutationFn: (date?: string) => exportDeliveryRecapXlsx(date),
  })

export const useReapproSimulationExport = () =>
  useMutation({
    mutationFn: (storeId: number) => exportReapproSimulationXlsx(storeId),
  })

export const useReapproSimulationAllStoresExport = () =>
  useMutation({
    mutationFn: () => exportReapproSimulationAllStoresXlsx(),
  })

export const useReapproSimulationAllStoresEmail = () =>
  useMutation({
    mutationFn: (data: { emails: string[]; sendToPilotage: boolean; referenceDate?: string }) =>
      requestReapproSimulationAllStoresXlsxByEmail(data),
  })

export const useDispatchSimulationAllStoresEmail = () =>
  useMutation({
    mutationFn: (data: { emails: string[]; sendToPilotage: boolean; referenceDate?: string }) =>
      requestDispatchSimulationAllStoresXlsxByEmail(data),
  })

export const useReapprosImport = () =>
  useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('importFile', file)

      const res = await gozokiApiWithAuth.post('/stores/reappro-needs/import-xlsx', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data
    },
  })

export const useImportReapproNeedModifications = () =>
  useMutation({
    mutationFn: importReapproNeedModifications,
  })

export const useImportReapproNeedModificationsBulk = () =>
  useMutation({
    mutationFn: importReapproNeedModificationsBulk,
  })

export const useStoreActionLogs = (storeId?: number) =>
  useQuery({
    queryKey: ['store-action-logs', storeId],
    queryFn: storeId ? () => fetchStoreActionLogs(storeId) : skipToken,
  })

export const useCpiInventorySummary = () =>
  useQuery({
    queryKey: ['cpi-inventory-summary'],
    queryFn: fetchCpiInventorySummary,
  })
